import { voiceDraftExpired } from './consoleVoice';
export interface VoiceDraft {
  context: string;
  id: string;
  created: number;
  blob: Blob;
  transcript: string;
  partial: boolean;
  duration: number;
}
// A one-way digest isolates links without writing their bearer credentials.
export async function voiceContext(token: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(`console-voice:${token}`),
  );
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}
function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('console-voice-drafts', 1);
    request.onupgradeneeded = () =>
      request.result.createObjectStore('drafts', { keyPath: 'context' });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(Error('draft_unavailable'));
  });
}
async function transact<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await database();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('drafts', mode);
    const request = run(transaction.objectStore('drafts'));
    transaction.oncomplete = () => {
      db.close();
      resolve(request.result);
    };
    transaction.onerror = transaction.onabort = () => {
      db.close();
      reject(Error('draft_unavailable'));
    };
  });
}
export async function saveVoiceDraft(draft: VoiceDraft): Promise<void> {
  await transact('readwrite', (store) => store.put(draft));
}
export async function deleteVoiceDraft(context: string): Promise<void> {
  await transact('readwrite', (store) => store.delete(context));
}
export async function loadVoiceDraft(context: string): Promise<VoiceDraft | null> {
  const value = await transact<VoiceDraft | undefined>(
    'readonly',
    (store) => store.get(context) as IDBRequest<VoiceDraft | undefined>,
  );
  if (!value) return null;
  if (value.context !== context || voiceDraftExpired(value.created, Date.now())) {
    await deleteVoiceDraft(context);
    return null;
  }
  return value;
}
